import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import FilePreview from './FilePreview';
import ExportModal from './ExportModal';
import TrialNotification from './TrialNotification';
import HelpIcon from './HelpIcon';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '../config.js';
import { authenticatedFetch, authenticatedFetchJson, authenticatedFetchBlob } from '../utils/api.js';
import { getHelpContent } from '../utils/helpContent.js';

const CURRENCIES = [
  { code: 'EUR', name: 'Euro' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'GBP', name: 'British Pound' },
  // Add more as needed
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [uploadedItems, setUploadedItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Sort and filter state
  const [sortBy, setSortBy] = useState('time'); // time, uploader, originalName
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [filterBy, setFilterBy] = useState('me'); // me only
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [companyMembers, setCompanyMembers] = useState([]);
  const [preview, setPreview] = useState(null); // { filename, rows, sheetNames, currentSheet, loading, error }
  const fileInputRef = useRef();
  const isInitialMount = useRef(true);
  const [scriptMode, setScriptMode] = useState('latin'); // 'latin' or 'cyrillic'
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportTargetFields, setExportTargetFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);

  // Currency converter state
  const [convCurrency, setConvCurrency] = useState('EUR');
  const [convAmount, setConvAmount] = useState('');
  const [convDate, setConvDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [convResult, setConvResult] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState('');
  const [companyData, setCompanyData] = useState(null);

  // Clipboard and history state
  const [clipboardData, setClipboardData] = useState(null); // 2D array of copied cells
  const [historyStack, setHistoryStack] = useState([]); // Array of previous states
  const [historyIndex, setHistoryIndex] = useState(-1); // Current position in history (-1 = no history)
  const MAX_HISTORY_SIZE = 50;
  
  // Auto-save function ref
  const autoSaveRef = useRef(null);

  useEffect(() => {
    // Fetch Taurus fields from backend
    authenticatedFetchJson(API_ENDPOINTS.TAURUS_FIELDS)
      .then(data => {
        setExportTargetFields(data.fields || []);
        setFieldsLoading(false);
      })
      .catch(() => setFieldsLoading(false));
  }, []);

  const fetchCompanyMembers = async () => {
    try {
      const data = await authenticatedFetchJson(API_ENDPOINTS.ORGANIZATION_MEMBERS);
      if (data.members) {
        setCompanyMembers(data.members);
      }
    } catch (error) {
      console.error('Error fetching company members:', error);
    }
  };

  const fetchFiles = async (reset = true) => {
    if (reset) {
      setLoadingFiles(true);
      setCurrentOffset(0);
    } else {
      setLoadingMore(true);
    }
    
    const offset = reset ? 0 : currentOffset;
    
    // Build query parameters
    const params = new URLSearchParams({
      limit: '20',
      offset: offset.toString(),
      sortBy,
      sortOrder,
    });
    
    if (filterBy !== 'all') {
      params.append('filterBy', filterBy);
    }
    if (dateFrom) {
      params.append('dateFrom', dateFrom);
    }
    if (dateTo) {
      params.append('dateTo', dateTo);
    }
    
    try {
      const data = await authenticatedFetchJson(`${API_ENDPOINTS.LIST_UPLOADS}?${params.toString()}`);
      if (reset) {
        setUploadedItems(data.uploads || []);
      } else {
        setUploadedItems(prev => [...prev, ...(data.uploads || [])]);
      }
      setHasMore(data.pagination?.hasMore || false);
      setCurrentOffset(offset + (data.uploads?.length || 0));
    } catch (error) {
      if (reset) {
        setUploadedItems([]);
      }
    }
    setLoadingFiles(false);
    setLoadingMore(false);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchFiles(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const data = await authenticatedFetchJson(API_ENDPOINTS.PROFILE);
      if (data.company) {
        setCompanyData(data.company);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchCompanyData();
    fetchCompanyMembers();
    // eslint-disable-next-line
  }, []);

  // Refetch files when sort or filter changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchFiles(true);
    // eslint-disable-next-line
  }, [sortBy, sortOrder, filterBy, dateFrom, dateTo]);



  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        setUploading(true);
        try {
          const data = await authenticatedFetchJson(API_ENDPOINTS.UPLOAD_FILE, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: file.name,
              type: file.type,
              size: file.size,
              data: base64,
            }),
          });
          setUploading(false);
          alert('Fajl uspešno otpremljen!');
          fetchFiles(true); // Reset and reload from beginning
        } catch (err) {
          setUploading(false);
          if (err.message !== 'Unauthorized - redirected to login') {
            alert('Greška pri povezivanju sa serverom.');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch and preview a file by ID
  const handleFileDownload = async (e, item) => {
    e.stopPropagation(); // Prevent triggering file click
    try {
      const blob = await authenticatedFetchBlob(`${API_ENDPOINTS.API_BASE_URL}/api/upload/${item.id}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.originalName || item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.message !== 'Unauthorized - redirected to login') {
        alert('Greška pri preuzimanju fajla.');
      }
    }
  };

  const handleFileDelete = async (item) => {
    try {
      const data = await authenticatedFetchJson(`${API_ENDPOINTS.API_BASE_URL}/api/upload/${item.id}`, {
        method: 'DELETE',
      });
      // Refresh the file list
      fetchFiles(true);
      // Clear preview if the deleted file was being viewed
      if (preview?.fileId === item.id) {
        setPreview(null);
      }
      alert(data.message || 'Fajl je uspešno obrisan.');
    } catch (err) {
      if (err.message !== 'Unauthorized - redirected to login') {
        alert('Greška pri povezivanju sa serverom.');
      }
    }
  };

  const handleFileClick = async (item) => {
    setPreview({ loading: true });
    // Reset history when loading a new file
    setHistoryStack([]);
    setHistoryIndex(-1);
    setClipboardData(null);
    // Close sidebar when opening a file
    setSidebarOpen(false);
    try {
      // Use the new /api/file/:id endpoint that returns JSON
      const res = await authenticatedFetch(`${API_ENDPOINTS.GET_FILE_PREVIEW}/${item.id}`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Greška pri preuzimanju fajla.' }));
        setPreview({ error: errorData.error || 'Greška pri preuzimanju fajla.' });
        return;
      }
      
      const data = await res.json();
      
      // Check if server returned base64 data requiring client-side parsing (fallback)
      if (data.requireClientParse && data.base64) {
        // Fallback: parse on client side
        const binary = atob(data.base64);
        const workbook = XLSX.read(binary, { type: 'binary' });
        const sheetNames = workbook.SheetNames || [];
        let rows = [];
        if (sheetNames.length > 0) {
          const ws = workbook.Sheets[sheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        }
        
        // Store base64 data for sheet switching
        setPreview({ 
          filename: data.filename || item.originalName || item.filename, 
          rows, 
          sheetNames, 
          currentSheet: 0,
          workbookData: data.base64, // Store base64 for sheet switching
          mimetype: data.contentType || item.mimeType,
          fileId: item.id
        });
        return;
      }
      
      // Normal response: server already parsed the file
      const sheetNames = data.sheetNames || [];
      const sheets = data.sheets || {};
      let rows = [];
      let currentSheet = 0;
      
      if (sheetNames.length > 0 && sheets[sheetNames[0]]) {
        rows = sheets[sheetNames[0]];
      }
      
      // Store all sheets data for switching between sheets
      // We'll store it in a format that handleSheetChange can use
      const allSheetsData = {};
      for (const sheetName of sheetNames) {
        if (sheets[sheetName]) {
          allSheetsData[sheetName] = sheets[sheetName];
        }
      }
      
      setPreview({ 
        filename: data.filename || item.originalName || item.filename, 
        rows, 
        sheetNames, 
        currentSheet,
        sheets: allSheetsData, // Store all sheets for switching
        mimetype: data.contentType || item.mimeType,
        fileId: item.id
      });
    } catch (err) {
      console.error('File click error:', err);
      setPreview({ error: 'Greška pri povezivanju sa serverom.' });
    }
  };

  // Add handler to switch sheets
  const handleSheetChange = (sheetIdx) => {
    if (!preview || !preview.sheetNames) return;
    let rows = [];
    
    // If sheets data is available from backend (preferred method)
    if (preview.sheets && preview.sheetNames[sheetIdx]) {
      const sheetName = preview.sheetNames[sheetIdx];
      rows = preview.sheets[sheetName] || [];
    } else if (preview.arrayBuffer) {
      // Fallback: parse from arrayBuffer
      const workbook = XLSX.read(preview.arrayBuffer, { type: 'array' });
      const ws = workbook.Sheets[preview.sheetNames[sheetIdx]];
      rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    } else if (preview.workbookData) {
      // Fallback: parse from base64
      const binary = atob(preview.workbookData);
      const workbook = XLSX.read(binary, { type: 'binary' });
      const ws = workbook.Sheets[preview.sheetNames[sheetIdx]];
      rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    }
    
    setPreview(prev => {
      // Preserve sheets object and update with current rows before switching
      const updatedSheets = { ...prev.sheets };
      if (prev.sheetNames && prev.sheetNames[prev.currentSheet]) {
        updatedSheets[prev.sheetNames[prev.currentSheet]] = prev.rows;
      }
      // Update sheets with new sheet's rows
      if (prev.sheetNames && prev.sheetNames[sheetIdx]) {
        updatedSheets[prev.sheetNames[sheetIdx]] = rows;
      }
      return { ...prev, rows, currentSheet: sheetIdx, sheets: updatedSheets };
    });
  };

  // Copy handler - extracts selected cells and copies to clipboard
  const handleCopy = (selectedRange) => {
    if (!preview || !preview.rows || !selectedRange) return;
    
    const minRow = Math.min(selectedRange.start.row, selectedRange.end.row);
    const maxRow = Math.max(selectedRange.start.row, selectedRange.end.row);
    const minCol = Math.min(selectedRange.start.col, selectedRange.end.col);
    const maxCol = Math.max(selectedRange.start.col, selectedRange.end.col);
    
    // Extract cell data as 2D array
    const copiedData = [];
    for (let i = minRow; i <= maxRow; i++) {
      const row = [];
      for (let j = minCol; j <= maxCol; j++) {
        const cellValue = preview.rows[i]?.[j] ?? '';
        row.push(cellValue);
      }
      copiedData.push(row);
    }
    
    // Format as TSV (tab-separated values)
    const tsv = copiedData.map(row => row.join('\t')).join('\n');
    
    // Store in state
    setClipboardData(copiedData);
    
    // Copy to system clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(tsv).catch(err => {
        console.error('Failed to copy to clipboard:', err);
        // Fallback to execCommand
        fallbackCopyToClipboard(tsv);
      });
    } else {
      // Fallback for older browsers
      fallbackCopyToClipboard(tsv);
    }
  };

  // Fallback copy method for older browsers
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  // Paste handler - parses clipboard data and pastes into selected range
  const handlePaste = async (selectedRange) => {
    if (!preview || !preview.rows || !selectedRange) return;
    
    let pasteData = null;
    
    // Try to read from system clipboard first
    if (navigator.clipboard && navigator.clipboard.readText) {
      try {
        const tsvText = await navigator.clipboard.readText();
        if (tsvText) {
          // Parse TSV into 2D array
          pasteData = tsvText.split('\n').map(row => row.split('\t'));
          // Update clipboardData state to keep it in sync
          setClipboardData(pasteData);
        }
      } catch (err) {
        console.error('Failed to read from clipboard:', err);
        // Fall through to use stored clipboardData
      }
    }
    
    // If clipboard read failed or not available, use stored clipboardData
    if (!pasteData) {
      if (clipboardData) {
        pasteData = clipboardData;
      } else {
        alert('No clipboard data available. Please copy cells first.');
        return;
      }
    }
    
    if (!pasteData || pasteData.length === 0) {
      alert('No data to paste.');
      return;
    }
    
    const startRow = selectedRange.start.row;
    const startCol = selectedRange.start.col;
    const maxRow = preview.rows.length - 1;
    const maxCol = preview.rows.reduce((max, row) => Math.max(max, row.length), 0) - 1;
    
    // Save current state to history before pasting
    const currentState = {
      rows: JSON.parse(JSON.stringify(preview.rows)),
      sheets: preview.sheets ? JSON.parse(JSON.stringify(preview.sheets)) : null,
      currentSheet: preview.currentSheet
    };
    
    // Use functional updates to ensure we have the latest values
    setHistoryIndex(prevIndex => {
      setHistoryStack(prevStack => {
        const newStack = prevStack.slice(0, prevIndex + 1);
        newStack.push(currentState);
        if (newStack.length > MAX_HISTORY_SIZE) {
          newStack.shift();
          return newStack;
        }
        return newStack;
      });
      const newIndex = prevIndex + 1;
      return newIndex >= MAX_HISTORY_SIZE ? MAX_HISTORY_SIZE - 1 : newIndex;
    });
    
    // Paste data starting at selected cell
    setPreview(prev => {
      if (!prev || !prev.rows) return prev;
      
      const newRows = prev.rows.map((row, i) => {
        if (i < startRow || i >= startRow + pasteData.length) {
          return row;
        }
        const pasteRow = pasteData[i - startRow];
        return row.map((cell, j) => {
          if (j < startCol || j >= startCol + (pasteRow?.length || 0)) {
            return cell;
          }
          return pasteRow[j - startCol] ?? '';
        });
      });
      
      // Ensure we have enough rows
      while (newRows.length < startRow + pasteData.length) {
        newRows.push([]);
      }
      
      // Ensure each row has enough columns
      pasteData.forEach((pasteRow, rowOffset) => {
        const targetRow = startRow + rowOffset;
        while (newRows[targetRow].length < startCol + pasteRow.length) {
          newRows[targetRow].push('');
        }
      });
      
      // Update sheets object
      const updatedSheets = { ...prev.sheets };
      if (prev.sheetNames && prev.sheetNames[prev.currentSheet]) {
        updatedSheets[prev.sheetNames[prev.currentSheet]] = newRows;
      }
      
      return { ...prev, rows: newRows, sheets: updatedSheets };
    });
    
    // Trigger auto-save after paste
    if (autoSaveRef.current) {
      autoSaveRef.current();
    }
  };

  // Add handler for cell editing - also updates sheets object
  const handleCellChange = (rowIdx, colIdx, newValue) => {
    // Save current state to history before making changes
    setPreview(prev => {
      if (!prev || !prev.rows) return prev;
      
      // Deep clone current state for history
      const currentState = {
        rows: JSON.parse(JSON.stringify(prev.rows)),
        sheets: prev.sheets ? JSON.parse(JSON.stringify(prev.sheets)) : null,
        currentSheet: prev.currentSheet
      };
      
      // Add to history stack (clear redo stack if we're not at the end)
      // Use functional updates to ensure we have the latest historyIndex
      setHistoryIndex(prevIndex => {
        setHistoryStack(prevStack => {
          const newStack = prevStack.slice(0, prevIndex + 1);
          newStack.push(currentState);
          // Limit history size
          if (newStack.length > MAX_HISTORY_SIZE) {
            newStack.shift();
            return newStack;
          }
          return newStack;
        });
        const newIndex = prevIndex + 1;
        return newIndex >= MAX_HISTORY_SIZE ? MAX_HISTORY_SIZE - 1 : newIndex;
      });
      
      const newRows = prev.rows.map((row, i) =>
        i === rowIdx ? row.map((cell, j) => (j === colIdx ? newValue : cell)) : row
      );
      // Update sheets object with new rows
      const updatedSheets = { ...prev.sheets };
      if (prev.sheetNames && prev.sheetNames[prev.currentSheet]) {
        updatedSheets[prev.sheetNames[prev.currentSheet]] = newRows;
      }
      return { ...prev, rows: newRows, sheets: updatedSheets };
    });
    
    // Trigger auto-save after cell change
    if (autoSaveRef.current) {
      autoSaveRef.current();
    }
  };

  // Undo handler - restores previous state from history
  const handleUndo = () => {
    if (historyIndex < 0 || historyStack.length === 0) {
      return; // Nothing to undo
    }
    
    const previousState = historyStack[historyIndex];
    if (!previousState) return;
    
    setPreview(prev => ({
      ...prev,
      rows: JSON.parse(JSON.stringify(previousState.rows)),
      sheets: previousState.sheets ? JSON.parse(JSON.stringify(previousState.sheets)) : prev.sheets
    }));
    
    setHistoryIndex(prevIndex => Math.max(-1, prevIndex - 1));
  };

  // Redo handler - restores next state from history
  const handleRedo = () => {
    if (historyIndex >= historyStack.length - 1) {
      return; // Nothing to redo
    }
    
    const nextState = historyStack[historyIndex + 1];
    if (!nextState) return;
    
    setPreview(prev => ({
      ...prev,
      rows: JSON.parse(JSON.stringify(nextState.rows)),
      sheets: nextState.sheets ? JSON.parse(JSON.stringify(nextState.sheets)) : prev.sheets
    }));
    
    setHistoryIndex(prevIndex => Math.min(historyStack.length - 1, prevIndex + 1));
  };

  // Handler for translating selected cells
  const handleTranslateSelected = async (selectedRange) => {
    if (!preview || !preview.rows || !selectedRange) return;
    const minRow = Math.min(selectedRange.start.row, selectedRange.end.row);
    const maxRow = Math.max(selectedRange.start.row, selectedRange.end.row);
    const minCol = Math.min(selectedRange.start.col, selectedRange.end.col);
    const maxCol = Math.max(selectedRange.start.col, selectedRange.end.col);
    // Collect values
    const cells = [];
    for (let i = minRow; i <= maxRow; i++) {
      for (let j = minCol; j <= maxCol; j++) {
        cells.push(preview.rows[i]?.[j] ?? '');
      }
    }
    // Call backend
    try {
      const res = await fetch(API_ENDPOINTS.TRANSLATE_TEXT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texts: cells, targetLang: 'sr', scriptMode }),
      });
      const data = await res.json();
      console.log('[Frontend Translation Response]', {
        ok: res.ok,
        status: res.status,
        data: data,
        hasTranslations: !!data.translations,
        translationsLength: data.translations?.length,
        sampleTranslations: data.translations?.slice(0, 3)
      });
      
      if (!res.ok) {
        console.error('[Translation Error]', data);
        alert(data.error || 'Translation failed.');
        return;
      }
      
      if (!data.translations || !Array.isArray(data.translations)) {
        console.error('[Translation Response Invalid]', {
          expected: 'array of translations',
          received: data
        });
        alert('Invalid translation response format.');
        return;
      }
      
      if (data.translations.length !== cells.length) {
        console.warn('[Translation Count Mismatch]', {
          expected: cells.length,
          received: data.translations.length
        });
      }
      
      // Update table - also update sheets object
      let idx = 0;
      setPreview(prev => {
        if (!prev || !prev.rows) return prev;
        const newRows = prev.rows.map((row, i) =>
          row.map((cell, j) => {
            if (i >= minRow && i <= maxRow && j >= minCol && j <= maxCol) {
              const translated = data.translations[idx++];
              return translated !== undefined ? translated : cell;
            }
            return cell;
          })
        );
        // Update sheets object with translated rows
        const updatedSheets = { ...prev.sheets };
        if (prev.sheetNames && prev.sheetNames[prev.currentSheet]) {
          updatedSheets[prev.sheetNames[prev.currentSheet]] = newRows;
        }
        console.log('[UI Updated]', {
          rowsUpdated: newRows.length,
          cellsUpdated: idx
        });
        return { ...prev, rows: newRows, sheets: updatedSheets };
      });
      
      // Trigger auto-save after translation
      if (autoSaveRef.current) {
        autoSaveRef.current();
      }
    } catch (err) {
      console.error('[Translation Exception]', err);
      alert('Translation failed: ' + err.message);
    }
  };

  // Export selected cells as CSV (open in modal for mapping)
  const handleExportSelected = (selectedRange) => {
    if (!preview || !preview.rows || !selectedRange) return;
    const minRow = Math.min(selectedRange.start.row, selectedRange.end.row);
    const maxRow = Math.max(selectedRange.start.row, selectedRange.end.row);
    const minCol = Math.min(selectedRange.start.col, selectedRange.end.col);
    const maxCol = Math.max(selectedRange.start.col, selectedRange.end.col);
    const data = [];
    for (let i = minRow; i <= maxRow; i++) {
      const row = [];
      for (let j = minCol; j <= maxCol; j++) {
        let cell = preview.rows[i]?.[j] ?? '';
        row.push(cell);
      }
      data.push(row);
    }
    setExportData(data);
    setExportModalOpen(true);
  };
  const handleExportModalConfirm = async (mapping, format = 'taurus') => {
    // Prepare mappedData and mapping for export
    if (!exportData || !mapping) return;
    
    // Validate that at least one field is mapped
    const mappedFields = Object.values(mapping).filter(v => v !== undefined && v !== null && v !== '');
    if (mappedFields.length === 0) {
      alert('Please map at least one field before exporting.');
      return;
    }
    
    try {
      // Determine endpoint and filename based on format
      const endpoint = format === 'csv' 
        ? `${API_ENDPOINTS.API_BASE_URL}/api/upload/export-csv`
        : API_ENDPOINTS.TAURUS_EXPORT;
      const filename = format === 'csv'
        ? `export-${new Date().toISOString().split('T')[0]}.csv`
        : `taurus-export-${new Date().toISOString().split('T')[0]}.txt`;
      
      // Prepare request body based on format
      let requestBody;
      if (format === 'csv') {
        requestBody = { 
          mappedData: exportData, 
          mapping,
          targetFields: exportTargetFields 
        };
      } else {
        // Map company data to Taurus SECTION1_FIELDS format
        const taurusCompanyData = companyData ? {
          'NAZIV': companyData.name || '',
          'PIB': companyData.pib || '',
          'MATICNI BROJ': companyData.mb || '',
          'PDV BROJ': companyData.pib || '', // Use PIB as PDV BROJ if not separate
          'SIFRA DELATNOSTI': '',
          'POSTA': companyData.postalCode || '',
          'MESTO': companyData.city || '',
          'ADRESA': companyData.address || '',
          'TELEFON': companyData.phone || '',
          'FAX': '',
          'MAIL': companyData.email || '',
          'WEB': companyData.website || '',
          'RACUN': '',
          'INTERNA SIFRA PARTNERA': ''
        } : {};
        
        requestBody = { 
          mappedData: exportData, 
          mapping,
          companyData: taurusCompanyData,
          documentData: {} // Can be extended later if needed
        };
      }
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        alert(`Export failed: ${errorText || 'Unknown error'}`);
        setExportModalOpen(false);
        setExportData(null);
        return;
      }
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err) {
      console.error('Export error:', err);
      alert(`Export failed: ${err.message || 'Unknown error'}`);
    }
    setExportModalOpen(false);
    setExportData(null);
  };
  const handleExportModalClose = () => {
    setExportModalOpen(false);
    setExportData(null);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConvLoading(true);
    setConvError('');
    setConvResult(null);
    try {
      const res = await fetch(`${API_ENDPOINTS.EXCHANGE_RATE}/convert?date=${convDate}&amount=${convAmount}`);
      const data = await res.json();
      setConvLoading(false);
      if (!res.ok) {
        setConvError(data.error || 'Greška pri konverziji.');
      } else {
        setConvResult(data.value);
      }
    } catch (err) {
      setConvLoading(false);
      setConvError('Greška pri povezivanju sa serverom.');
    }
  };

  return (
    <div style={{minHeight: '100vh', background: colors.backgroundLight, position: 'relative', display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease'}}>
      <TrialNotification companyData={companyData} />
      {uploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: isDark ? 'rgba(15,23,42,0.8)' : 'rgba(255,255,255,0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            border: `6px solid ${colors.border}`,
            borderTop: `6px solid ${colors.primary}`,
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, flexShrink: 0 }}>
        <Navbar scriptMode={scriptMode} onScriptModeChange={setScriptMode} />
      </div>
      <div style={{display: 'flex', flex: 1, minHeight: 0, position: 'relative'}}>
        {/* Sidebar */}
        <Sidebar
          uploadedItems={uploadedItems}
          loadingFiles={loadingFiles}
          fileInputRef={fileInputRef}
          handleFileChange={handleFileChange}
          handleUploadClick={handleUploadClick}
          handleFileClick={handleFileClick}
          handleFileDownload={handleFileDownload}
          handleFileDelete={handleFileDelete}
          sidebarOpen={sidebarOpen}
          selectedFileId={preview?.fileId || null}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(field, order) => {
            setSortBy(field);
            setSortOrder(order);
          }}
          filterBy={filterBy}
          onFilterChange={setFilterBy}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          companyMembers={companyMembers}
        />
        {/* Sidebar toggle button (always visible, not inside aside) */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'absolute',
            top: 16,
            left: sidebarOpen ? '360px' : '40px',
            transform: sidebarOpen ? 'translateX(-50%)' : 'translateX(0)',
            zIndex: 21, // Higher than sidebar to appear on top
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: '50%',
            transition: 'left 0.3s ease, transform 0.3s ease, background-color 0.2s, border-color 0.2s',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 4px rgba(30,41,59,0.08)',
            cursor: 'pointer',
            transition: 'left 0.3s ease, transform 0.3s ease',
          }}
          aria-label={sidebarOpen ? t('sidebar.hideFiles') : t('sidebar.showFiles')}
        >
          <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'}`} style={{color: colors.primary, fontSize: 18}}></i>
        </button>
        {/* Main content area */}
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0}}>
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 0}}>
            <FilePreview
              preview={preview}
              setPreview={setPreview}
              onSheetChange={handleSheetChange}
              onCellChange={handleCellChange}
              onTranslateSelected={handleTranslateSelected}
              scriptMode={scriptMode}
              onExportSelected={handleExportSelected}
              onCopy={handleCopy}
              onPaste={handlePaste}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={historyIndex >= 0 && historyStack.length > 0}
              onAutoSaveReady={(autoSaveFn) => {
                autoSaveRef.current = autoSaveFn;
              }}
            />
          </div>
        </div>
      </div>
      <ExportModal
        open={exportModalOpen}
        onClose={handleExportModalClose}
        data={exportData}
        onConfirm={handleExportModalConfirm}
        targetFields={exportTargetFields}
        companyData={companyData}
        documentData={{}}
      />
      {fieldsLoading && <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:isDark ? 'rgba(15,23,42,0.8)' : 'rgba(255,255,255,0.5)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,color:colors.primary}}>Loading Taurus fields...</div>}
    </div>
  );
} 